import { Controller } from "@hotwired/stimulus";

// Keeps the read-only "Price by location" table in sync with the base-price editor, so edits preview before
// they're saved. A row shows only while its location is selected in Locations; and its price/tax/total are
// recomputed from the current Amount + Tax mode + that location's tax rate (a data attribute) whenever either
// changes. On the demo every row has a rate; on live only locations Nexus has already priced do, so a
// newly-added location shows "—" until the edit is saved. Wired on the product form, which catches the
// multiselect/select bubbling "change" and the amount input's "input".
export default class extends Controller {
  static targets = ["row", "empty", "table"];

  connect() {
    // Leave the server-rendered amounts untouched on load; only sync which rows are visible.
    this.syncVisibility();
  }

  refresh() {
    this.syncVisibility();
    const amountCents = this.amountCents();
    const mode = this.pricingMode();
    this.rowTargets.forEach((row) => {
      if (!row.classList.contains("hidden")) this.updateRow(row, amountCents, mode);
    });
  }

  syncVisibility() {
    const selected = this.selectedLocationIds();
    let anyVisible = false;
    this.rowTargets.forEach((row) => {
      const show = selected.has(row.dataset.locationId);
      row.classList.toggle("hidden", !show);
      anyVisible ||= show;
    });
    if (this.hasTableTarget) this.tableTarget.classList.toggle("hidden", !anyVisible);
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle("hidden", anyVisible);
  }

  updateRow(row, amountCents, mode) {
    const rate = parseFloat(row.dataset.taxRate);
    // No amount entered, or no rate to resolve tax with (a live location Nexus hasn't priced yet): show "—".
    if (amountCents === null || Number.isNaN(rate)) {
      this.setCells(row, null, null, null);
      return;
    }

    let unit, tax, total;
    if (mode === "inclusive") {
      // The entered amount is tax-inclusive; MenuPriceDTO reports the unit price tax-inclusive too (== total),
      // with the embedded tax broken out.
      const preTax = Math.round(amountCents / (1 + rate));
      unit = amountCents;
      tax = amountCents - preTax;
      total = amountCents;
    } else {
      unit = amountCents;
      tax = Math.round(amountCents * rate);
      total = amountCents + tax;
    }
    this.setCells(row, unit, tax, total);
  }

  setCells(row, unit, tax, total) {
    const cents = { price: unit, tax, total };
    row.querySelectorAll("[data-cell]").forEach((cell) => {
      const value = cents[cell.dataset.cell];
      cell.textContent = value === null ? "—" : this.formatCents(value);
    });
  }

  selectedLocationIds() {
    return new Set(
      Array.from(this.element.querySelectorAll('input[name="record[locations][]"]'))
        .map((input) => input.value)
        .filter((value) => value !== "")
    );
  }

  amountCents() {
    const dollars = parseFloat(this.element.querySelector('input[name="record[price_dollars]"]')?.value);
    return Number.isNaN(dollars) ? null : Math.round(dollars * 100);
  }

  pricingMode() {
    return this.element.querySelector('input[name="record[pricing_mode]"]')?.value || "exclusive";
  }

  formatCents(cents) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(cents / 100);
  }
}
